import { IconButton } from "@material-ui/core";
import { MoreVertRounded } from "@material-ui/icons";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { PiShieldWarningBold } from "react-icons/pi";
import { FaUserTag } from "react-icons/fa";
import api from "../../../services/api";
import { toast } from "react-toastify";
import { COLORS } from "../../../assets/colors";

const tableHeaders = [
  "Avatar",
  "First Name",
  "Last Name",
  "Email",
  "Phone",
  "Joined",
  "Role",
  "Actions",
];

const AdminTable = ({ admins, onRefresh }) => {
  const { currentUser } = useSelector((state) => state.auth);
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const ddref = useRef();

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      setIsOpen(false);
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const handleOutsideClick = (event) => {
    if (ddref.current && !ddref.current.contains(event.target)) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = (item) => {
    if (activeItem?.id === item.id) {
      setIsOpen(!isOpen);
    } else {
      setActiveItem(item);
      setIsOpen(true);
    }
  };

  const onDemoteAdmin = async (data) => {
    try {
      const res = await api.post(`admin/user/demote/${data.id}`);
      if (res.status === 200) {
        toast.success("Admin demoted to user successfully");
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to demote admin");
    }
    setIsOpen(false);
  };

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            {tableHeaders.map((header, index) => (
              <th key={index}>{header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {admins?.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              <TableCell>
                <div className="avatar-wrapper">
                  <img
                    src={
                      row.avatar_url ||
                      `https://ui-avatars.com/api/?name=${row.first_name}&background=random`
                    }
                    alt="avatar"
                  />
                </div>
              </TableCell>
              <TableCell className="bold">{row.first_name}</TableCell>
              <TableCell className="bold">{row.last_name}</TableCell>
              <TableCell>{row.email}</TableCell>
              <TableCell>{row.mobile_number}</TableCell>
              <TableCell>{moment(row.created_at).format("DD MMM, YYYY")}</TableCell>
              <TableCell>
                <StatusBadge is_admin={row.role === "admin"}>
                  {row.role}
                </StatusBadge>
              </TableCell>
              <TableCell>
                <ActionWrapper>
                  <IconButton onClick={() => toggleDropdown(row)}>
                    <MoreVertRounded />
                  </IconButton>
                  {activeItem?.id === row.id && isOpen && (
                    <DropdownMenu ref={ddref}>
                      {currentUser?.email === "support@gosawari.com" && (
                        <MenuItem onClick={() => onDemoteAdmin(row)} className="danger">
                          <FaUserTag /> Demote to User
                        </MenuItem>
                      )}
                    </DropdownMenu>
                  )}
                </ActionWrapper>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

export default AdminTable;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  position: relative;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;

  th {
    padding: 1rem;
    background: #F8FAFC;
    color: ${COLORS.adminTextSecondary};
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    position: sticky;
    top: 0;
    z-index: 10;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
  transition: background 0.2s ease;

  &:hover {
    background: rgba(56, 189, 248, 0.03);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${COLORS.adminTextPrimary};
  font-size: 0.875rem;
  vertical-align: middle;

  &.bold {
    font-weight: 600;
  }

  .avatar-wrapper {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    overflow: hidden;
    border: 2px solid #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  display: inline-flex;
  
  background: ${props => props.is_admin ? "rgba(56, 189, 248, 0.1)" : "rgba(100, 116, 139, 0.1)"};
  color: ${props => props.is_admin ? COLORS.adminAccent : COLORS.adminTextSecondary};
  text-transform: capitalize;
`;

const ActionWrapper = styled.div`
  position: relative;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  z-index: 100;
  min-width: 180px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
`;

const MenuItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  width: 100%;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: ${COLORS.adminTextPrimary};
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.2s;

  svg {
    font-size: 1.1rem;
    opacity: 0.7;
  }

  &:hover {
    background: #F1F5F9;
  }

  &.danger {
    color: ${COLORS.adminError};
    &:hover {
      background: rgba(239, 68, 68, 0.05);
    }
  }
`;
